import type {
	AdapterStatus,
	AudioPlaybackService,
	BlendedAudioPlaybackService,
	BlendedAudioPlaybackServiceArgs,
	BlendSnapshot,
	PlaybackStatus,
} from "../../application/ports";
import { WebAudioAdapter } from "./WebAudioAdapter";
import type { PlaybackStore } from "@/lib/application/stores/playbackStore";
import { createStore, useStore } from "zustand";
import { combine, subscribeWithSelector } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { isMobileDevice } from "@/lib/utils";
import { StreamingAudioContextAdapter } from "./StreamingAudioContextAdapter";
import type { ResolvedTrackModel } from "@coffitivity/shared";

export interface BlendAdapterState {
	playbackStatus: PlaybackStatus;
	volume: number;
}

export interface BlendStoreProps {
	state: Record<string, BlendAdapterState>;
	addBlendState: (trackId: string, initialState: BlendAdapterState) => void;
	getCurrentBlendSnapshot: () => BlendSnapshot;
	removeBlendState: (trackId: string) => void;
	updateBlendPlaybackStatus: (trackId: string, status: PlaybackStatus) => void;
	updateBlendVolume: (trackId: string, volume: number) => void;
}

export class BlendedAudioAdapter implements BlendedAudioPlaybackService {
	// public properties
	public data: ResolvedTrackModel;
	public preferredMimeType: "mp3" | "ogg";
	public usesAudioContext = true;
	public onPlay: (() => void) | null = null;
	public onPause: (() => void) | null = null;
	public onEnded: (() => void) | null = null;
	public tracks: AudioPlaybackService[] = []; // audio adapters for each track in the blend
	public store; // audio adapter state store for the blend
	public useBlendStateHook: () => BlendStoreProps;

	// private properties
	private playbackStore: PlaybackStore; // global playback store
	private masterVolume: number;
	private previousBlendState: { trackId: string; status: string }[] = [];

	constructor(_args: BlendedAudioPlaybackServiceArgs) {
		// Deep clone so Immer's freeze on the playback store never affects our local copy
		this.data = structuredClone(_args.track);
		this.playbackStore = _args.store;
		this.preferredMimeType = _args.preferredMimeType ?? "mp3";
		this.masterVolume = _args.masterVolume ?? 1;

		const initialBlendState: BlendStoreProps["state"] =
			_args.track.blend!.reduce(
				(acc, blend) => {
					acc[blend.track.id] = {
						playbackStatus: "UNINITIALIZED",
						volume: blend.volume,
					};
					return acc;
				},
				{} as BlendStoreProps["state"],
			);

		this.store = createStore<BlendStoreProps>()(
			subscribeWithSelector(
				immer(
					combine({ state: initialBlendState }, (set) => ({
						updateBlendPlaybackStatus: (
							trackId: string,
							status: PlaybackStatus,
						) => {
							set((state) => {
								if (state.state[trackId]) {
									state.state[trackId].playbackStatus = status;
								}
							});
						},
						updateBlendVolume: (trackId: string, volume: number) => {
							set((state) => {
								if (state.state[trackId]) {
									state.state[trackId].volume = volume;
								}
							});
						},
						addBlendState: (
							trackId: string,
							initialState: BlendAdapterState,
						) => {
							set((state) => {
								state.state[trackId] = initialState;
							});
						},
						getCurrentBlendSnapshot: (): BlendSnapshot => {
							const state = this.store.getState().state;
							return Object.entries(state).map(([trackId, blendState]) => ({
								trackId,
								volume: blendState.volume,
							}));
						},
						removeBlendState: (trackId: string) => {
							set((state) => {
								delete state.state[trackId];
							});
						},
					})),
				),
			),
		);

		this.useBlendStateHook = () => {
			const store = useStore(this.store);
			return store;
		};
	}

	private updateAdapterState(
		trackId: string,
		playbackStatus?: PlaybackStatus,
		volume?: number,
	): void {
		if (playbackStatus) {
			this.store.getState().updateBlendPlaybackStatus(trackId, playbackStatus);
		}
		if (volume) {
			this.store.getState().updateBlendVolume(trackId, volume);
		}
	}

	async load(): Promise<void> {
		try {
			const blendSpec = this.data.blend!;

			const trackAdapters = await Promise.all(
				blendSpec.map(async (blend) => {
					if (blend.track.type !== "TRACK") {
						throw new Error(
							`Blend sub-track must be a TRACK type: ${blend.track.id}`,
						);
					}

					let adapter: AudioPlaybackService;
					if (isMobileDevice()) {
						adapter = new WebAudioAdapter({
							store: this.playbackStore,
							isSubTrack: true,
							track: blend.track,
							masterVolume: this.masterVolume,
							preferredMimeType: this.preferredMimeType,
							trackVolume: blend.volume,
							updateAdapterState: this.updateAdapterState.bind(this),
						});
					} else {
						adapter = new StreamingAudioContextAdapter({
							store: this.playbackStore,
							isSubTrack: true,
							track: blend.track,
							masterVolume: this.masterVolume,
							preferredMimeType: this.preferredMimeType,
							trackVolume: blend.volume,
							updateAdapterState: this.updateAdapterState.bind(this),
						});
					}

					await adapter.load();

					return adapter;
				}),
			);

			this.tracks = trackAdapters;
		} catch (error) {
			console.error("Error loading blended tracks:", error);
			throw error; // Rethrow the error to be handled by the caller
		}
	}

	async play(
		_blendTrackId?: string,
		_overridePastState?: boolean,
	): Promise<void> {
		try {
			// Update the state so user see a quick updates
			if (this.playbackStore.getState().currentTrack?.id !== this.data.id) {
				this.playbackStore
					.getState()
					.setCurrentTrack(structuredClone(this.data));
			}

			if (_blendTrackId) {
				const track = this.tracks.find((t) => t.data.id === _blendTrackId);

				if (track) {
					await track.play();
				}
				// TODO: throw error if track not found?
			} else if (_overridePastState) {
				// if we're overriding past state, we need to play just all tracks
				const playPromises = await Promise.allSettled(
					this.tracks.map((track) => track.play()),
				);

				// if all of the tracks failed to play, we throw an error and keeping moving on to ensure the UI is updated
				if (playPromises.every((p) => p.status === "rejected")) {
					throw new Error("All tracks failed to play");
				}
			} else {
				if (
					this.previousBlendState.length > 0 &&
					this.previousBlendState.every((t) => t.status === "PAUSED")
				) {
					// if all tracks were paused, we need to play all tracks
					const playPromises = await Promise.allSettled(
						this.tracks.map((track) => track.play()),
					);

					// if all of the tracks failed to play, we throw an error and keeping moving on to ensure the UI is updated
					if (playPromises.every((p) => p.status === "rejected")) {
						throw new Error("All tracks failed to play");
					}
				} else if (this.previousBlendState.length > 0) {
					// if there's a previous blend state, we need to restore it otherwise just play all tracks
					const trackPlayPromises: Promise<void>[] = [];
					for (const track of this.tracks) {
						const previousTrackState = this.previousBlendState.find(
							(t) => t.trackId === track.data.id,
						);

						if (previousTrackState) {
							if (previousTrackState.status === "PLAYING") {
								trackPlayPromises.push(track.play());
							}
						}
					}
					// Wait for all tracks to start playing before updating status
					await Promise.allSettled(trackPlayPromises);
				} else {
					// if there's no previous blend state, we need to play all tracks
					const playPromises = await Promise.allSettled(
						this.tracks.map((track) => track.play()),
					);

					// if all of the tracks failed to play, we throw an error and keeping moving on to ensure the UI is updated
					if (playPromises.every((p) => p.status === "rejected")) {
						throw new Error("All tracks failed to play");
					}
				}
			}

			// Only set status to PLAYING after all async operations are complete
			this.playbackStore.getState().setCurrentTrackStatus("PLAYING");

			this.onPlay?.();
		} catch (error) {
			console.error("Error playing blended tracks:", error);
			throw error; // Rethrow the error to be handled by the caller
		}
	}

	async pause(trackId?: string): Promise<void> {
		// set the previous blend state before we pause the tracks
		this.previousBlendState = this.tracks.map((track) => ({
			trackId: track.data.id,
			status: track.isPlaying() ? "PLAYING" : "PAUSED",
		}));

		// ! Make sure to pause playback as soon as possible and prior to the actual pause event
		// this will ensure that the playback state is updated in the store and the UI correctly
		if (trackId) {
			const otherTracks = this.tracks.filter((t) => t.data.id !== trackId);
			const areOtherTracksPaused = otherTracks.every(
				(track) => track.isPlaying() === false,
			);
			const track = this.tracks.find((t) => t.data.id === trackId);
			if (track) {
				if (areOtherTracksPaused) {
					this.playbackStore.getState().setCurrentTrackStatus("PAUSED");
				}
				await track.pause();
			}
		} else {
			this.playbackStore.getState().setCurrentTrackStatus("PAUSED");
			await Promise.all(this.tracks.map((track) => track.pause()));
		}

		this.onPause?.();
	}

	async stop(): Promise<void> {
		await Promise.all(this.tracks.map((track) => track.stop()));
		this.playbackStore.getState().setCurrentTrackStatus("STOPPED");
		this.onPause?.(); // same handler for now
	}

	setLoop(loop: boolean): void {
		for (const track of this.tracks) {
			track.setLoop(loop);
		}
	}

	setTrackVolume(volume: number, trackId: string): void {
		const track = this.tracks.find((t) => t.data.id === trackId);
		if (track) {
			track.setTrackVolume(volume);
		}
	}

	isPlaying(): boolean {
		return this.tracks.some((track) => track.isPlaying());
	}

	async cleanup(): Promise<void> {
		await Promise.all(this.tracks.map((track) => track.cleanup()));
		this.tracks = [];
	}

	status(): AdapterStatus {
		const blendStatus = this.tracks.map((track) => track.status());
		return {
			trackId: this.data.id,
			status: this.isPlaying() ? "PLAYING" : "PAUSED",
			error: null,
			trackVolume: this.masterVolume,
			blendStatus,
		};
	}

	async addBlendTrack(
		_track: ResolvedTrackModel,
		_volume: number = 0.5,
	): Promise<void> {
		if (_track.type !== "TRACK") {
			return;
		}

		// Prevent duplicates
		if (this.tracks.some((t) => t.data.id === _track.id)) {
			return;
		}

		// maintain mutable adapter-local blend state
		this.data.blend!.push({
			track: _track,
			volume: _volume,
		});

		let adapter: AudioPlaybackService;

		if (isMobileDevice()) {
			adapter = new WebAudioAdapter({
				store: this.playbackStore,
				isSubTrack: true,
				track: _track,
				masterVolume: this.masterVolume,
				preferredMimeType: this.preferredMimeType,
				trackVolume: _volume,
				updateAdapterState: this.updateAdapterState.bind(this),
			});
		} else {
			adapter = new StreamingAudioContextAdapter({
				store: this.playbackStore,
				isSubTrack: true,
				track: _track,
				masterVolume: this.masterVolume,
				preferredMimeType: this.preferredMimeType,
				trackVolume: _volume,
				updateAdapterState: this.updateAdapterState.bind(this),
			});
		}

		await adapter.load();
		adapter.setLoop(true);

		this.tracks.push(adapter);
		this.store.getState().addBlendState(_track.id, {
			playbackStatus: "PAUSED",
			volume: _volume,
		});

		// If the blend is currently playing, start the new track too
		if (this.isPlaying()) {
			await adapter.play();
		}
	}

	getSnapshot(): BlendSnapshot {
		return this.store.getState().getCurrentBlendSnapshot();
	}

	async removeBlendTrack(_trackId: string): Promise<void> {
		const trackIndex = this.tracks.findIndex((t) => t.data.id === _trackId);
		if (trackIndex === -1) {
			return;
		}

		const adapter = this.tracks[trackIndex];
		await adapter.stop();
		await adapter.cleanup();

		this.tracks.splice(trackIndex, 1);
		this.store.getState().removeBlendState(_trackId);

		// Remove from previous blend state tracking
		this.previousBlendState = this.previousBlendState.filter(
			(t) => t.trackId !== _trackId,
		);

		// maintain mutable adapter-local blend state
		this.data.blend = this.data.blend!.filter((b) => b.track.id !== _trackId);
	}
}
